// Footer — used on all public pages
// Mobile: single column stack via CSS class
import type { ReactNode } from 'react';
import { useThemeColors } from "../hooks/useThemeColors";
import { useTheme } from "../contexts/ThemeContext";

const FOOTER_STYLE = `
  @media (max-width: 1023px) {
    .footer-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    .footer-inner { padding: 36px 20px 28px !important; }
  }
  @media (max-width: 639px) {
    .footer-blog-link { display: none !important; }
    .footer-link-item a { min-height: 44px; display: flex; align-items: center; }
  }
`

// PRI-10/PRI-310: removed dead-end links (Docs, Changelog) and the placeholder
// social URLs that were all pointing to "#". Restore Docs + social handles when
// the Web SDK EPIC ships and the real handles exist.

// PRI-512: About/company content lives on the www marketing site — footer
// links point there absolutely so the app carries no in-app About content.
const WWW = 'https://www.primateintelligence.ai'

const COLUMNS = [
  {
    title: 'Product',
    links: [
      { label: 'Primate Vision', href: '/' },
      // PRI-523: canonical pricing lives on www
      { label: 'Pricing', href: `${WWW}/pricing` },
      { label: 'Blog', href: `${WWW}/blog` },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Values', href: `${WWW}/values` },
      { label: 'Careers', href: `${WWW}/careers` },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookie-policy' },
    ],
  },
]

// PRI-10: social media URLs hidden until real handles exist. Empty array
// renders nothing in the social icon strip below — no more href="#" dead links.
const SOCIALS: { label: string; href: string; icon: ReactNode }[] = []

export default function Footer() {
  const c = useThemeColors();
  useTheme(); // retained for potential future theme-aware logo
  return (
    <footer style={{ background: c.bgSecondary, borderTop: `1px solid ${c.border}` }}>
      <style>{FOOTER_STYLE}</style>
      <div className="footer-inner" style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 56px 40px' }}>
        <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '1fr repeat(3, auto)', gap: '48px', marginBottom: 48 }}>
          {/* Brand column */}
          <div>
            <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', marginBottom: 16 }}>
              <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                <span style={{ fontSize: 13, fontWeight: 300, color: c.textPrimary, letterSpacing: '-0.02em' }}>Primate</span>
                <span style={{ fontSize: 13, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.02em' }}>Intelligence</span>
              </div>
            </a>
            <p style={{ fontSize: 13, color: c.textMuted, lineHeight: 1.6, maxWidth: 260, margin: '0 0 20px' }}>
              Real-time video intelligence API, built on predictive world models.
            </p>
            {/* Social icons */}
            <div style={{ display: 'flex', gap: 12 }}>
              {SOCIALS.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  style={{ color: c.textMuted, textDecoration: 'none', transition: 'color 150ms' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = c.textPrimary }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {COLUMNS.map(col => (
            <div key={col.title}>
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12 /* 12px floor (PRI-535) */, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textPrimary, fontWeight: 600, marginBottom: 16 }}>
                {col.title}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {col.links.map(link => (
                  <li
                    key={link.href}
                    className={`footer-link-item${link.label === 'Blog' ? ' footer-blog-link' : ''}`}
                  >
                    <a
                      href={link.href}
                      style={{ fontSize: 13, color: c.textMuted, textDecoration: 'none', transition: 'color 150ms', minHeight: 40, display: 'flex', alignItems: 'center' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = c.textPrimary }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 12, color: c.textMuted, margin: 0 }}>
            © 2026 Primate AI, Inc. · San Jose, CA
          </p>
          <p style={{ fontSize: 12 /* 12px floor (PRI-535) */, color: c.textMuted, margin: 0, fontFamily: '"JetBrains Mono", monospace' }}>
            api.primateintelligence.ai
          </p>
        </div>
      </div>
    </footer>
  )
}
